import { useCallback, useEffect, useRef } from 'react';

const RESET_EVENTS = ['pointerdown', 'keydown'] as const;

/**
 * Fires `onTimeout` after `timeoutMs` of no pointer or keyboard activity.
 * Returns a `reset()` function to restart the timer programmatically
 * (e.g. when a modal is open and the timeout fires but shouldn't interrupt).
 */
export function useIdleTimeout(
    timeoutMs: number,
    onTimeout: () => void
): { reset: () => void } {
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onTimeoutRef = useRef(onTimeout);
    onTimeoutRef.current = onTimeout;

    const reset = useCallback(() => {
        if (timerRef.current !== null) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => {
            onTimeoutRef.current();
        }, timeoutMs);
    }, [timeoutMs]);

    useEffect(() => {
        reset();
        RESET_EVENTS.forEach(event => document.addEventListener(event, reset));
        return () => {
            if (timerRef.current !== null) {
                clearTimeout(timerRef.current);
            }
            RESET_EVENTS.forEach(event =>
                document.removeEventListener(event, reset)
            );
        };
    }, [reset]);

    return { reset };
}
